import axios, { AxiosResponse } from 'axios';
import { apiClient } from '../apiClient';
import { MachineType } from './machinesTypes';
import { Site } from '../sites/sites';
import { DailyMachine } from '../dailyReports/dailyMachines';

interface User {
    _id: string;
    codeProC: string;
    lastName: string;
    firstName: string;
    jobId: string;
    username: string;
    profileId: string;
    latitude: number;
    longitude: number;
    color: string;
    phoneNumber?: string;
    isSST?: boolean;
    arrivalDate?: string;
    departureDate?: string;
    updatedAt?: string;
    createdAt?: string;
}

export interface Machine {
    crudStatus: number;
    _id: string;
    codeProC: string;
    designation: string;
    machineTypeId: MachineType;
    userId?: User;
    price?: number;
    priceType?: number;
    updatedAt?: string;
    createdAt?: string;
    usageCount?: {
        total: number;
        dailyTransfersPlanned: number;
        dailyTransfersRides: number;
        dailyMachines: number;
    };
    model?: string;
    startDate?: string;
    endDate?: string;
    baseName: string;
}

interface MachinesResponse {
    message: string;
    data: Machine[];
}

export interface GetMachinesQueries {
    active?: boolean;
    withUsage?: boolean;
}

export const getAllMachines = async (queries?: GetMachinesQueries): Promise<MachinesResponse> => {
    try {
        const response: AxiosResponse<MachinesResponse> = await apiClient.get('/machines', { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface UpdateMachineBody {
    designation?: string;
    codeProC?: string;
    machineTypeId?: string;
    userId?: string | null;
    crudStatus?: number;
    model?: string;
    startDate?: string | null;
    endDate?: string | null;
    baseName?: string;
    priceType?: number | null;
    price?: number | null;
}

export interface MachineResponse {
    message: string;
    data: Machine;
}

export const updateMachine = async (machineId: string, data: UpdateMachineBody): Promise<MachineResponse> => {
    try {
        const response: AxiosResponse<MachineResponse> = await apiClient.patch('/machines/' + machineId, data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface CreateMachineBody {
    designation: string;
    codeProC: string;
    machineTypeId: string;
    userId?: string | null;
    crudStatus?: number;
    model?: string;
    startDate?: string | null;
    endDate?: string | null;
    baseName: string;
    priceType?: number | null;
    price?: number | null;
}

export const createMachine = async (data: CreateMachineBody): Promise<MachineResponse> => {
    try {
        const response: AxiosResponse<MachineResponse> = await apiClient.post('/machines', data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface MachineWithLastLocation {
    _id: string;
    latestState: number;
    latestDate: string;
    designation: string;
    type: string;
    conducFirstName: string;
    conducLastName: string;
    conducColor: string;
    conducUsername: string;
    codeProC: string;
    siteName: string;
    siteId: string;
}

export interface MachinesWithLastLocationsResponse {
    message: string;
    data: MachineWithLastLocation[];
}

export const getMachineWithLastLocation = async (date: Date): Promise<MachinesWithLastLocationsResponse> => {
    try {
        const response: AxiosResponse<MachinesWithLastLocationsResponse> = await apiClient.get(
            '/daily-machines/locations/date/' + date.toISOString()
        );
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface MonthlyMachine extends Machine {
    dailyMachinesByDate: {
        date: string;
        daily: { dailyMachine: DailyMachine; user: User; site: Site }[];
    }[];
}

export interface GetMonthlyMachineParams {
    date: string;
}

export interface MonthlyMachinesResponse {
    data: MonthlyMachine[];
    message: string;
}

export const getMonthlyMachines = async (params: GetMonthlyMachineParams): Promise<MonthlyMachinesResponse> => {
    try {
        const response: AxiosResponse<MonthlyMachinesResponse> = await apiClient.get('/machines/monthly/' + params.date);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};
